import { useEffect, useState } from "react";
import { toolContentPath } from "../lib/apiPaths";
import { apiUrl } from "../lib/tenantOrigin";

type ToolOutputResult = {
  output: unknown | undefined;
  loading: boolean;
};

const empty: ToolOutputResult = { output: undefined, loading: false };
const cache = new Map<string, unknown>();

export function cacheToolOutput(entryId: string, output: unknown) {
  cache.set(entryId, output);
}

export function useToolOutput(entryId: string, enabled: boolean) {
  const externalApiUrl = apiUrl();
  const [result, setResult] = useState<ToolOutputResult>(() => {
    const cached = cache.get(entryId);
    return cached !== undefined ? { output: cached, loading: false } : empty;
  });

  useEffect(() => {
    if (!enabled) return;
    if (cache.has(entryId)) {
      // oxlint-disable-next-line react/react-compiler -- blocks React Compiler; remove when fixed
      setResult({ output: cache.get(entryId), loading: false });
      return;
    }

    const controller = new AbortController();
    setResult({ output: undefined, loading: true });

    void fetch(`${externalApiUrl}${toolContentPath(entryId)}`, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (res) => {
        if (controller.signal.aborted) return;
        if (res.status === 204 || !res.ok) {
          setResult(empty);
          return;
        }
        const output = await res.json();
        if (!controller.signal.aborted) {
          cache.set(entryId, output);
          setResult({ output, loading: false });
        }
      })
      .catch(() => {});

    return () => controller.abort();
  }, [entryId, enabled, externalApiUrl]);

  return result;
}
